import React, { useEffect, useState } from 'react';

import initAdobe, { setAdobeConsent } from 'state/adobe/init';
import { initGoogleTagManager } from 'utils/google-tag-manager';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

declare global {
  interface Window {
    OneTrust?: {
      OnConsentChanged: (callback: (event: any) => void) => void;
      ToggleInfoDisplay: () => void;
      [key: string]: any;
    };
    OnetrustActiveGroups?: string;
  }
}

type ConsentCategory = 'C0001' | 'C0002' | 'C0003' | 'C0004' | 'C0005';

type ConsentGroups = {
  C0001: number; // Strictly Necessary
  C0002: number; // Performance
  C0003: number; // Functional
  C0004: number; // Targeting
  C0005: number; // Social Media
};

const SDK_CATEGORY_MAPPING: Record<string, readonly ConsentCategory[]> = {
  ADOBE_EXPERIENCE_PLATFORM: ['C0004'],
};

const canTrackForSDK = (sdkName: string, consentGroups: ConsentGroups): boolean => {
  const requiredCategories: readonly ConsentCategory[] = SDK_CATEGORY_MAPPING[sdkName] ?? [];

  if (requiredCategories.length === 0) {
    return false;
  }

  // All categories must be active (AND logic)
  return requiredCategories.every(category => consentGroups[category as keyof ConsentGroups] === 1);
};

export const OneTrustCookieBanner = () => {
  // OneTrust SDK is loaded synchronously via index.html, guaranteed to be available
  const otSdkLoaded = typeof window !== 'undefined' && !!window.OneTrust;

  const [consentGroupsSetInLocalStorage, setConsentGroupsSetInLocalStorage] = useState(false);

  const getConsentGroups = (consentState: String[]) => {
    const groups: ConsentGroups = {
      C0001: 0, // Strictly necessary
      C0002: 0, // Performance
      C0003: 0, // Functional
      C0004: 0, // Targeting
      C0005: 0, // Social Media
    };

    return Object.keys(groups).reduce((accumulator, group) => {
      if (consentState.includes(group)) {
        accumulator[group as keyof ConsentGroups] = 1;
      }
      return accumulator;
    }, groups);
  };

  const manageCookieTracking = (consentGroups: ConsentGroups) => {
    // Adobe Experience Platform - initialize if consent is granted
    // Subsequent consents: update consent state to allow collection
    const hasAdobeConsent = canTrackForSDK('ADOBE_EXPERIENCE_PLATFORM', consentGroups);
    if (hasAdobeConsent) {
      initAdobe();
      setAdobeConsent(true);
    } else {
      setAdobeConsent(false);
    }

    // Google Tag Manager - initialize if any non-necessary consent category is active
    // GTM tags will respect their own categories via OneTrust auto-blocking
    const hasAnyNonStrictlyNecessaryConsent = Object.entries(consentGroups).some(
      ([category, value]) => category !== 'C0001' && value === 1
    );
    if (hasAnyNonStrictlyNecessaryConsent) {
      initGoogleTagManager();
    }
  };

  // Registers oneTrust consent change handler
  useEffect(() => {
    const applyConsent = (consentState: string[]) => {
      const consentGroups = getConsentGroups(consentState);
      manageCookieTracking(consentGroups);
      LocalStorage.setItem(StorageKeys.ONETRUST_COOKIE_CONSENT_GROUPS, consentGroups);
    };

    const id = setInterval(() => {
      if (window?.OneTrust && otSdkLoaded) {
        // Listen for future consent changes (user clicks confirm)
        window.OneTrust.OnConsentChanged((event: any) => {
          applyConsent(event?.detail ?? []);
        });

        // Apply current consent state on initial load
        const activeGroups = window.OnetrustActiveGroups;
        if (activeGroups) {
          applyConsent(activeGroups.split(',').filter(g => g.trim() !== ''));
        }

        setConsentGroupsSetInLocalStorage(true);
      }
    }, 1000);
    if (consentGroupsSetInLocalStorage) {
      clearInterval(id);
    }
    return () => clearInterval(id);
  }, [otSdkLoaded, consentGroupsSetInLocalStorage]);

  return <div data-testid="one-trust-cookie-banner" />;
};
